
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as api from "@/lib/api";
import { queryKeys } from "@/lib/query-keys";

export function useQuestions(
  examSlug: string,
  page = 1,
  perPage = 20,
  categoryId?: number,
  search?: string,
  filters?: {
    fromNumber?: number;
    toNumber?: number;
    isMultiple?: boolean;
    hasExplanation?: boolean;
  },
) {
  return useQuery({
    queryKey: queryKeys.questions.list(examSlug, {
      page,
      perPage,
      categoryId,
      search,
      fromNumber: filters?.fromNumber,
      toNumber: filters?.toNumber,
      isMultiple: filters?.isMultiple,
      hasExplanation: filters?.hasExplanation,
    }),
    queryFn: () =>
      api.getQuestions(examSlug, page, perPage, categoryId, search, filters),
    enabled: !!examSlug,
    placeholderData: (prev) => prev,
  });
}

export function useQuestion(examSlug: string, id: number) {
  return useQuery({
    queryKey: queryKeys.questions.detail(examSlug, id),
    queryFn: () => api.getQuestion(examSlug, id),
    enabled: !!examSlug && Number.isFinite(id),
  });
}

export function useRandomQuestions(
  examSlug: string,
  count = 10,
  categoryId?: number,
) {
  return useQuery({
    queryKey: queryKeys.questions.random(examSlug, count, categoryId),
    queryFn: () => api.getRandomQuestions(examSlug, count, categoryId),
    enabled: !!examSlug,
    staleTime: 0,
  });
}

export function useCategories(examSlug: string) {
  return useQuery({
    queryKey: queryKeys.categories.list(examSlug),
    queryFn: () => api.getCategories(examSlug),
    enabled: !!examSlug,
    staleTime: 5 * 60 * 1000,
  });
}

export function useDraftQuestions(examSlug: string, page = 1) {
  return useQuery({
    queryKey: queryKeys.questions.drafts(examSlug, page),
    queryFn: () => api.getDraftQuestions(examSlug, page),
    enabled: !!examSlug,
    placeholderData: (prev) => prev,
  });
}

export function useGenerateExplanation(examSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.generateExplanation(examSlug, id),
    onSuccess: (question) => {
      qc.setQueryData(queryKeys.questions.detail(examSlug, question.id), question);
      qc.invalidateQueries({ queryKey: queryKeys.questions.lists(examSlug) });
    },
  });
}

export function usePublishQuestion(examSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.publishQuestion(examSlug, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.questions.all });
    },
  });
}

export function useGenerateQuestions(examSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof api.generateQuestions>[1]) =>
      api.generateQuestions(examSlug, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.questions.all });
    },
  });
}
